import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import MarkdownIt from 'markdown-it';
import isEmpty from 'lodash/isEmpty';
import striptags from 'striptags';
import dayjs from 'dayjs';
import Utils from '../../../components/components/common/utils.js';
import Input from '../../utilities/input_parser';
import {
  changeAdditionalDetails,
  onAddRepeatedFieldItems,
  onRemoveRepeatedFieldItems,
  onSetDetailsErrors,
} from '../../actions/orders';
import ErrorTooltip, { errorFieldClass } from './ErrorTooltip';
import StyledInputContainer from './StyledInputContainer';
import DatePicker from '../../../components/components/common/date_picker.js';
import { isValidEmail, isValidPhoneNumber, detectMob } from '../../utilities/helperUtility';
import { PLACEHOLDER_PHONE } from '../../../components/constants';

const md = new MarkdownIt();

const ItemWrapper = styled.div`
  .dp-wrapper {
    width: 100%;
  }
  input {
    background: transparent;
  }      
}`;

const convertToUtc = (datetime, timeZone) => {
  if (!datetime || datetime.format() === 'Invalid Date') return null;
  const datetimeInTimezone = dayjs.tz(datetime.format('YYYY-MM-DD HH:mm'), timeZone);
  return datetimeInTimezone.utc().format('YYYY-MM-DD HH:mm');
};

const parseDefaultValue = (field) => {
  switch (field.type) {
    case 'dropdown':
    case 'radio':
    case 'checkbox':
      return { defaultValue: field.options };
    default:
      return { defaultValue: '' };
  }
};

const parseDefaultPlaceholder = (field) => {
  switch (field.name) {
    case 'phone':
      return field.placeholder || PLACEHOLDER_PHONE;
    default:
      return field.placeholder;
  }
};

const setError = (field, message, errors) => {
  errors.push({ key: field, value: message, fieldChanged: false });
  return errors;
};

const checkValidField = (values, s, key, errors) => {
  switch (s.name) {
    case 'phone':
      if (!isValidPhoneNumber(values)) setError(key, `${s.label}: Invalid phone format`, errors);
      break;
    case 'email':
      if (!isValidEmail(values)) setError(key, `${s.label}: Invalid email format`, errors);
      break;
    default:
      break;
  }
};

const defaultKey = (field, index) => field.key || (`field${index}`);

class DatePickerView extends Component {
  onSetDateTime(datetime, timeZone) {
    const { field, index, onChange } = this.props;
    if (datetime.toString() === 'Invalid Date') {
      onChange(field, null, index);
    } else {
      const convert = convertToUtc(datetime, timeZone);
      const value = !convert || convert.toString() === 'Invalid Date' ? null : convert;
      onChange(field, value, index);
    }
  }

  selectedInTimeZone() {
    const { currentValue, timeZone } = this.props;
    if (!currentValue) return null;
    const dateConvert = dayjs.utc(currentValue).tz(timeZone);
    return !!currentValue && dateConvert.toString() !== 'Invalid Date' ? dateConvert : null;
  }

  placeholder() {
    const { dateFormat } = this.props;
    return Utils.dateFormat(dateFormat).toUpperCase() || '';
  }

  render() {
    const { timeZone, dateFormat, timeInterval } = this.props;

    return (
      <ItemWrapper>
        <DatePicker
          placeholderText={this.placeholder()}
          showTimeSelect
          selected={this.selectedInTimeZone()}
          onChange={(val) => this.onSetDateTime(val, timeZone)}
          className="form-control "
          timeZone={timeZone}
          dFormat={dateFormat}
          timeInterval={timeInterval}
        />
      </ItemWrapper>
    );
  }
}

function FieldInput(props) {
  const {
    errors,
    currentValue,
    defaultValue,
    fieldKey,
    label,
    onChange,
    placeholder,
    style,
    translation,
    type,
    field,
  } = props;
  const { classStyle, message } = errorFieldClass(errors, fieldKey);
  const customStyle = style.body;

  return (
    <div className="field-input-container pure-form" key={fieldKey}>
      <label
        className="control-label"
        style={{
          textAlign: style.body?.inputsLabelsTextAlign,
        }}
      >
        {Utils.convertMarkdownLabelToReactHtml(label)}
      </label>
      <StyledInputContainer styled={customStyle}>
        { field.name === 'date_time'
          ? (
          <DatePickerView
            {...props}
          />
          )
          : (
          <Input
            classStyle={classStyle}
            currentSetting={{ [fieldKey]: currentValue }}
            defaultSetting={{ [fieldKey]: defaultValue }}
            fieldKey={fieldKey}
            includeBlank
            onChange={onChange}
            placeholder={placeholder}
            style={customStyle}
            translation={translation}
            type={type}
          />
          )}
        { message !== ''
          && <ErrorTooltip message={message} />}
      </StyledInputContainer>
    </div>
  );
}

class RepeatedBookingFields extends Component {
  componentDidMount() {
    const {
      additionalDetails, bookingFields, quantity, totalQuantity,
    } = this.props;
    const quantityValue = totalQuantity || quantity || 1;

    if (isEmpty(additionalDetails)) {
      this.addRepeatedFields(bookingFields, quantityValue);
    } else {
      this.changeRepeatedFields(additionalDetails, bookingFields, quantityValue);
    }
  }

  changeRepeatedFields(additionalDetails, bookingFields, quantity) {
    const { onRemoveRepeatedFields } = this.props;
    const repeatedField = bookingFields.find((field) => !!field.repeated);

    if (repeatedField) {
      const fieldsCount = additionalDetails[repeatedField.key].length;

      if (fieldsCount < quantity) {
        this.addRepeatedFields(bookingFields, quantity - fieldsCount);
      } else if (fieldsCount > quantity) {
        onRemoveRepeatedFields(bookingFields, quantity);
      }
    }
  }

  addRepeatedFields(bookingFields, quantity) {
    const { onAddRepeatedField } = this.props;
    for (let i = 1; i <= quantity; i++) onAddRepeatedField(bookingFields);
  }

  componentDidUpdate(prevProps) {
    const {
      additionalDetails, bookingFields, quantity, totalQuantity, willUpdate,
    } = this.props;
    const quantityValue = totalQuantity || quantity || 1;
    const prevQuantityValue = prevProps.totalQuantity || prevProps.quantity || 1;
    if (quantityValue && quantityValue !== prevQuantityValue) {
      this.changeRepeatedFields(additionalDetails, bookingFields, quantityValue);
    }
    if (willUpdate) willUpdate();
  }

  defaultRepeatedValues(field) {
    const { additionalDetails } = this.props;
    const { key } = field;
    if (key) return additionalDetails[key] || [];
    return [];
  }

  render() {
    const {
      bookingFields, errors, style, onChange, translation, timeZone, dateFormat, timeInterval,
    } = this.props;
    const repeated = bookingFields.filter((field) => !!field.repeated);
    const repeatedValueCount = repeated[0] ? this.defaultRepeatedValues(repeated[0]).length : 0;
    const arrayOfIndex = [...Array(repeatedValueCount).keys()].map((x) => x++);

    return (
      <div className="repeated-fields">
        {
          arrayOfIndex.map((index) => (
              <div className="repeated-items" key={index} style={{ border: `1px solid ${style.body.color}` }}>
                {
                  repeated.map((field, i) => {
                    const fieldKey = defaultKey(field, i);
                    const { defaultValue } = parseDefaultValue(field);
                    const placeholder = parseDefaultPlaceholder(field);
                    const value = this.defaultRepeatedValues(field)[index];
                    const key = `${fieldKey}-${index}`;
                    const currentValue = value || '';

                    return (
                      <FieldInput
                        key={key}
                        fieldKey={key}
                        currentValue={currentValue}
                        defaultValue={defaultValue}
                        errors={errors}
                        label={field.label}
                        onChange={(fKey, fValue) => onChange(field, fValue, index)}
                        placeholder={placeholder}
                        style={style}
                        translation={translation}
                        type={field.type}
                        field={field}
                        index={i}
                        timeZone={timeZone}
                        timeInterval={timeInterval}
                        dateFormat={dateFormat}
                      />
                    );
                  })
                }
              </div>
          ))
        }
      </div>
    );
  }
}

function NonRepeatedBookingFields(props) {
  const {
    additionalDetails, bookingFields, errors, onChange, style, translation, timeZone, dateFormat, timeInterval,
  } = props;
  const nonRepeated = bookingFields.filter((field) => !field.repeated);

  return (
    <div className="non-repeated-fields">
      {nonRepeated.map((field, i) => {
        const { key } = field;
        const { defaultValue } = parseDefaultValue(field);
        const placeholder = parseDefaultPlaceholder(field);
        const currentValue = additionalDetails[key] || '';

        return (
          <FieldInput
            key={key}
            currentValue={currentValue}
            defaultValue={defaultValue}
            errors={errors}
            fieldKey={key}
            label={field.label}
            onChange={(fKey, fValue) => onChange(field, fValue)}
            placeholder={placeholder}
            style={style}
            translation={translation}
            type={field.type}
            field={field}
            index={i}
            timeZone={timeZone}
            dateFormat={dateFormat}
            timeInterval={timeInterval}
          />
        );
      })}
    </div>
  );
}

class BookingFieldsBlock extends Component {
  isMobile = detectMob();

  componentDidMount() {
    const { getValidationFn } = this.props;
    if (getValidationFn) getValidationFn(this.validateAdditionalDetailsBound);
  }

  validateAdditionalDetailsBound = this.validateAdditionalDetails.bind(this);

  validateAdditionalDetails() {
    const {
      additionalDetails, bookingFields, onSetErrors, translation,
    } = this.props;
    const errors = [];
    const settings = bookingFields || [];
    const required = translation.key('requiredField') || 'Required';

    settings.forEach((s) => {
      let values = additionalDetails[s.key];
      if (s.required) {
        const label = striptags(md.render(s.label));
        const isFile = s.type === 'file';
        if (!Array.isArray(values)) values = [values || ''];
        if (values.length === 0) {
          setError(`${s.key}-0`, `${label}: ${required}`, errors);
        }

        values.map((v, i) => {
          const key = s.repeated ? `${s.key}-${i}` : s.key;
          const isCheckbox = Array.isArray(v);
          if (
            (isCheckbox && v.length === 0)
            || (isFile && !v)
            || (!isCheckbox && !isFile && !v.trim())
          ) {
            setError(key, `${label}: ${required}`, errors);
          }
        });
      }

      if (Array.isArray(values)) {
        values.map((v, i) => {
          if (v) {
            const key = s.repeated ? `${s.key}-${i}` : s.key;
            checkValidField(v, s, key, errors);
          }
        });
      } else if (values) {
        const key = s.repeated ? `${s.key}-0` : s.key;
        checkValidField(values, s, key, errors);
      }
    });

    onSetErrors(errors);

    return errors;
  }

  willUpdate() {
    const { willUpdate } = this.props;
    if (willUpdate) willUpdate();
  }

  render() {
    const { bookingFields, style, translation } = this.props;
    const props = { ...this.props, ...{ willUpdate: this.willUpdate.bind(this) } };
    const containerClass = `booking-fields-container${this.isMobile ? ' mobile-no-scroll' : ''}`;

    return (
      <div>
        { bookingFields.length > 0
          && (
          <div className={containerClass} style={{ borderColor: `${style.body.color}` }}>
            <div className="additional-field-label">
              {translation.key('additionalInformation') || 'Additional information'}
            </div>
            <NonRepeatedBookingFields {...props} />
            <RepeatedBookingFields {...props} />
          </div>
          )}
      </div>
    );
  }
}

BookingFieldsBlock.propTypes = {
  bookingFields: PropTypes.array.isRequired,
  onChange: PropTypes.func,
};

export default connect(
  ({ order, settings }) => ({
    quantity: order.quantity,
    additionalDetails: order.additionalDetails,
    errors: order.additionalDetailsValidationErrors,
    bookingFields: settings.bookingFields,
    style: settings.style,
  }),
  (dispatch) => ({
    onChange: (field, value, index) => dispatch(changeAdditionalDetails({ field, value, index })),
    onAddRepeatedField: (fieldsSettings) => dispatch(onAddRepeatedFieldItems({ fieldsSettings })),
    onRemoveRepeatedFields: (fieldsSettings, quantity) => dispatch(onRemoveRepeatedFieldItems({
      fieldsSettings,
      quantity,
    })),
    onSetErrors: (errors) => dispatch(onSetDetailsErrors(errors)),
  }),
)(BookingFieldsBlock);
